import { useMutation } from '@tanstack/react-query';
import { SourcingChannelStatus } from 'api-clients/monolith';
import { Loader2 } from 'lucide-react';

import { updateCampaignStatus } from '@/api-clients/update-campaign-status';
import { useConfirmation } from '@/components/base/confirmation';
import { useToaster } from '@/components/base/toaster';
import { SetReachPublisherStatus } from '@/components/pages/channels/campaigns/use-reach-channels';
import { useChannelsStore } from '@/components/pages/channels/use-channels-store';
import { Switch } from '@/components/ui/switch';
import { REACH } from '@/types/channels';

import { useMessages } from './use-messages';

interface ToggleCampaignProps {
  sourcingPurchaseId: string;
  reachCampaignId: string | null;
  status: SourcingChannelStatus;
  channel: string;
  setReachPublisherStatus?: SetReachPublisherStatus;
  isDisabled?: boolean;
}

export function ToggleCampaign({
  sourcingPurchaseId,
  reachCampaignId,
  status,
  channel,
  setReachPublisherStatus,
  isDisabled,
}: ToggleCampaignProps) {
  const refetchReachChannels = useChannelsStore(s => s.refetchReachChannels);
  const setActiveCampaignStatus = useChannelsStore(
    s => s.setActiveCampaignStatus,
  );
  const { notifySuccess, notifyError } = useToaster();
  const { confirm } = useConfirmation();

  const isCampaignEnabled = status === 'active';
  const updatedStatus = status === 'active' ? 'stopped' : 'active';

  const {
    confirmTitle,
    confirmDescription,
    confirmButtonLabel,
    notifySuccessMessage,
    notifyErrorMessage,
  } = useMessages(isCampaignEnabled);

  const { mutate: toggleCampaignStatus, isPending } = useMutation({
    mutationFn: updateCampaignStatus,
    onSuccess: async () => {
      notifySuccess({
        message: notifySuccessMessage,
      });
      if (reachCampaignId && setReachPublisherStatus)
        setReachPublisherStatus(
          sourcingPurchaseId,
          reachCampaignId,
          updatedStatus,
        );
      else setActiveCampaignStatus(sourcingPurchaseId, updatedStatus);

      if (channel === REACH) await refetchReachChannels?.();
    },
    onError: () => {
      notifyError({
        message: notifyErrorMessage,
      });
    },
  });

  const handleToggleRequest = async () => {
    const confirmed = await confirm({
      title: confirmTitle,
      description: confirmDescription,
      confirmLabel: confirmButtonLabel,
    });

    if (confirmed) {
      toggleCampaignStatus({
        sourcingPurchaseId,
        requestBody: {
          enabled: !isCampaignEnabled,
          channel,
          reach_campaign_id: reachCampaignId,
        },
      });
    }
  };

  if (isPending) {
    return <Loader2 className="text-fx-primary h-4 animate-spin" />;
  }

  return (
    <>
      <Switch
        checked={isCampaignEnabled}
        onCheckedChange={handleToggleRequest}
        disabled={isDisabled}
      />
    </>
  );
}
